// src/components/SharePointSearch/SharePointFileList.tsx

import * as React from "react";
import { useState, useCallback } from "react";
import { SharePointSearchFile } from "../../interfaces/sharepointSearchTypes";
import { FileTypeIcon, ChevronIcon } from "./SharePointIcons";
import { spColors } from "./sharepointSearchStyles";
import { formatFileSize, formatDate, getFileExtension } from "./sharepointSearchUtils";

interface SharePointFileListProps {
  files: SharePointSearchFile[];
  totalResults: number;
  moreResultsAvailable: boolean;
}

/**
 * Compact expandable file list for SharePoint search results (Option B).
 * Each file renders as a collapsed row with chevron, file type icon, name, and date.
 * Expanding reveals the summary snippet and metadata.
 */
const SharePointFileList: React.FC<SharePointFileListProps> = ({
  files,
  totalResults,
  moreResultsAvailable,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!files || files.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {files.map((file) => {
        const fileId = `${file.drive_id}-${file.item_id}`;
        const isExpanded = expandedIds.has(fileId);
        const ext = getFileExtension(file.mime_type, file.name);
        const shortDate = formatDate(file.last_modified);

        return (
          <div
            key={fileId}
            style={{
              borderRadius: "6px",
              border: `1px solid ${spColors.border}`,
              backgroundColor: "white",
              overflow: "hidden",
            }}
          >
            {/* Collapsed row */}
            <div
              onClick={() => toggleExpand(fileId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleExpand(fileId);
                }
              }}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${file.name}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <ChevronIcon isExpanded={isExpanded} />
              <FileTypeIcon mimeType={file.mime_type} fileName={file.name} />
              <span
                style={{
                  flex: 1,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: spColors.foreground,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {file.name}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  color: spColors.mutedForeground,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {shortDate}
              </span>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div
                style={{
                  padding: "0 10px 10px 42px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {file.summary && (
                  <div
                    style={{
                      fontSize: "11px",
                      lineHeight: "1.5",
                      color: spColors.foreground,
                      padding: "6px 10px",
                      borderLeft: `3px solid ${spColors.tealBorder}`,
                      backgroundColor: spColors.quoteBg,
                      borderRadius: "0 4px 4px 0",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: file.summary.replace(
                        /\*\*(.*?)\*\*/g,
                        '<strong style="color: #038387">$1</strong>'
                      ),
                    }}
                  />
                )}
                <span style={{ fontSize: "10px", color: spColors.mutedForeground }}>
                  {formatFileSize(file.size)} &middot; .{ext}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {/* Footer summary */}
      <p
        style={{
          fontSize: "11px",
          color: spColors.mutedForeground,
          marginTop: "4px",
          fontStyle: "italic",
        }}
      >
        Showing {files.length} of {totalResults} results
        {moreResultsAvailable && " (more available)"}
      </p>
    </div>
  );
};

export default SharePointFileList;
